import { useCallback, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NewsPlatform } from '../types/news.types';
import { getNewsCta } from '../utils/news.utils';
import { useRecordNewsEvents } from './useNewsReader';

export interface NewsCtaSource {
  id: string;
  ctaLabel: string | null;
  ctaUrl: string | null;
}

/**
 * Gestionnaire de clic du bouton d'action d'une nouveauté, à poser sur un lien
 * dont le `href` est déjà l'adresse du bouton :
 * - déclare `cta_clicked` ;
 * - pour un chemin interne, navigue dans l'application sans la recharger (sauf
 *   clic « ouvrir ailleurs » : molette, Ctrl/Cmd/Maj — le navigateur fait alors
 *   son travail) ; une adresse http(s) s'ouvre d'elle-même dans un nouvel onglet.
 *
 * `onInternalNavigation` est appelé après une navigation interne (la modale s'en
 * sert pour se fermer).
 */
export function useNewsCtaClick(platform: NewsPlatform, onInternalNavigation?: () => void) {
  const navigate = useNavigate();
  const recordEvents = useRecordNewsEvents(platform);

  return useCallback(
    (news: NewsCtaSource, event: MouseEvent<HTMLAnchorElement>) => {
      const cta = getNewsCta(news);
      if (!cta) return;
      recordEvents([{ newsId: news.id, types: ['cta_clicked'] }]);

      const opensElsewhere =
        event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
      if (cta.external || opensElsewhere || event.defaultPrevented) return;

      event.preventDefault();
      navigate(cta.url);
      onInternalNavigation?.();
    },
    [navigate, recordEvents, onInternalNavigation],
  );
}
